import localforage from 'localforage'
import {
  RECENTLY_VIEWED_MAX_EVENTS,
  RECENTLY_VIEWED_WINDOW_MS,
  type AdminPageView,
} from './recentlyViewed.types'

// Read/append helpers for the rolling list of admin page views. Writes go
// straight to localForage (rather than through the React Query `useLocalForageKey`
// hook) so recording a view on every navigation doesn't churn React state; the
// homepage list reads the same key through React Query and refetches on mount.
// `storageKey` is identity-scoped — see `buildAdminRecentlyViewedStorageKey`.

export const readAdminPageViews = async (storageKey: string): Promise<AdminPageView[]> => {
  const stored = await localforage.getItem<AdminPageView[]>(storageKey)
  return Array.isArray(stored) ? stored : []
}

// Drop events outside the trailing window and cap total length (newest kept).
const prune = (events: AdminPageView[], now: number): AdminPageView[] => {
  const fresh = events.filter(event => now - event.viewedAt <= RECENTLY_VIEWED_WINDOW_MS)
  return fresh.length > RECENTLY_VIEWED_MAX_EVENTS
    ? fresh.slice(fresh.length - RECENTLY_VIEWED_MAX_EVENTS)
    : fresh
}

// Serializes all writes. Each record() is a read-modify-write and the tracker
// fires one per navigation; without serialization two near-simultaneous
// navigations both read the same array and the later `setItem` clobbers the
// earlier one, dropping an event and skewing counts. Chaining every write on a
// single tail promise guarantees each append observes the prior write's result.
// The leading `.catch` keeps one failed write from breaking the chain for
// subsequent writes.
let writeQueue: Promise<unknown> = Promise.resolve()

export const recordAdminPageView = (storageKey: string, view: AdminPageView): Promise<void> => {
  const next = writeQueue
    .catch(() => undefined)
    .then(async () => {
      const existing = await readAdminPageViews(storageKey)
      const pruned = prune([...existing, view], view.viewedAt)
      await localforage.setItem(storageKey, pruned)
    })
  writeQueue = next
  return next
}
